"use client";

import { useId } from "react";
import { CalendarDays } from "@/components/icons";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { DeliveryWindowPicker } from "@/components/cart/delivery-window-picker";
import {
  earliestSelectableDeliveryDate,
  formatDeliveryWindow,
  type DeliveryWindow,
} from "@/lib/checkout/delivery-window";
import { cn } from "@/lib/utils";

export type SavedAddress = FunctionReturnType<
  typeof api.addresses.queries.listMine
>[number];

export function DeliveryWindowField({
  deliveryWindow,
  leadTimeDays,
  onDeliveryWindowChange,
}: {
  deliveryWindow: DeliveryWindow;
  leadTimeDays: number;
  onDeliveryWindowChange: (window: DeliveryWindow) => void;
}) {
  const labelId = useId();
  const label = formatDeliveryWindow(deliveryWindow);
  return (
    <div>
      <Label id={labelId} size="sm">
        Requested delivery window *
      </Label>
      <DeliveryWindowPicker
        window={deliveryWindow}
        earliestDate={earliestSelectableDeliveryDate(leadTimeDays)}
        onWindowChange={onDeliveryWindowChange}
      >
        <Button
          type="button"
          variant="outline"
          aria-label={`Requested delivery window: ${label || "Choose dates"}`}
          className={cn(
            "mt-1 w-full justify-between font-normal",
            !label && "text-muted-foreground",
          )}
          size="sm"
        >
          {label || "Choose dates"}
          <CalendarDays className="h-4 w-4" />
        </Button>
      </DeliveryWindowPicker>
    </div>
  );
}

export function DockOnSiteField({
  hasDockOnSite,
  onHasDockOnSiteChange,
}: {
  hasDockOnSite: boolean;
  onHasDockOnSiteChange: (hasDock: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox
        checked={!hasDockOnSite}
        onCheckedChange={(checked) => onHasDockOnSiteChange(checked !== true)}
      />
      No loading dock
    </label>
  );
}
